import {
  DragOverlay,
  defaultDropAnimationSideEffects,
  useDraggable,
} from "@dnd-kit/core";
import { InteractiveContainer } from "@jugl-web/ui-components/cross-platform/InteractiveContainer";
import { TaskCardPill } from "@jugl-web/ui-components/cross-platform/tasks/TaskCardPill";
import { TaskLabelContainer } from "@jugl-web/ui-components/cross-platform/tasks/TaskLabelGroup";
import { cx, useTranslations, useUniqueId } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { FC, HTMLAttributes, ReactNode } from "react";
import { createPortal } from "react-dom";
import Highlighter from "react-highlight-words";
import { PreviewTask } from "@jugl-web/rest-api/tasks";
import { TaskCardDraggableData } from "../../types";
import { shortenTaskTitle } from "../../utils";
import { ReactComponent as BellIcon } from "./assets/bell.svg";
import { ReactComponent as CheckBlankIcon } from "./assets/check-blank.svg";
import { ReactComponent as OrderIcon } from "./assets/order-icon.svg";
import { ReactComponent as RecurringWarningIcon } from "./assets/recurring-warning.svg";
import { ReactComponent as RecurringIcon } from "./assets/recurring.svg";
import { ReactComponent as ScheduleMdIcon } from "./assets/schedule-md.svg";

interface TaskCardChecklist {
  completedItemsCount: number;
  totalItemsCount: number;
  isOverdue: boolean;
}

export interface TaskCardField {
  id: string;
  value: ReactNode;
  textColor: string;
  backgroundColor: string;
  hint?: ReactNode;
  startIcon?: ReactNode;
}

export interface TaskCardProps extends HTMLAttributes<HTMLDivElement> {
  dueDate: Date | null;
  isCompleted?: boolean;
  isOverdue: boolean;
  isUnread?: boolean;
  taskId: string;
  title: string;
  fields: TaskCardField[];
  checklist?: TaskCardChecklist;
  columnId?: string;
  draggable?: boolean;
  highlightedText?: string;
  recurrence?: {
    isFutureTask: boolean;
    isPaused: boolean;
  };
  isFormBased: boolean;
  shortId?: string;
  task?: PreviewTask;
  onTaskPreview?: (taskId: string) => void;
  openFutureTaskDialog?: (task: PreviewTask) => void;
}

export const TaskCard: FC<TaskCardProps> = (props) => {
  const {
    taskId,
    isUnread,
    title,
    dueDate,
    isOverdue,
    isCompleted,
    checklist,
    fields,
    highlightedText,
    className,
    style,
    draggable,
    columnId,
    recurrence,
    isFormBased,
    shortId,
    task,
    onClick,
    onTaskPreview,
    openFutureTaskDialog,
    ...divProps
  } = props;

  const { t } = useTranslations();
  const { formatDateTimeLabel } = useFormattedDate();

  const uniqueId = useUniqueId();

  const isFutureTask = !!recurrence?.isFutureTask;

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `task-${taskId}-${uniqueId}`,
    disabled: !draggable,
    data: {
      type: "task",
      taskId,
      columnId,
      isFutureTask,
    } satisfies TaskCardDraggableData,
  });

  const shortenedTitle = shortenTaskTitle(title);

  const openTaskPreview = () => {
    if (isFutureTask && task && openFutureTaskDialog) {
      openFutureTaskDialog(task);
      return;
    }
    onTaskPreview?.(taskId);
  };

  return (
    <>
      {isDragging &&
        createPortal(
          <DragOverlay
            dropAnimation={{
              sideEffects: defaultDropAnimationSideEffects({
                styles: {
                  active: {
                    opacity: "1",
                  },
                },
              }),
            }}
          >
            <TaskCard
              {...props}
              draggable={false}
              className="border-px rotate-2 border-dashed border-primary-600 opacity-80"
            />
          </DragOverlay>,
          document.body
        )}
      <InteractiveContainer
        ref={setNodeRef}
        className={cx(
          "relative touch-pan-y overflow-hidden rounded-2xl bg-white px-4 pb-4 transition-shadow",
          {
            "shadow-task-card hover:shadow-task-card-hovered": !isDragging,
          },
          isUnread ? "pt-7" : "pt-4",
          className
        )}
        style={style}
        onClick={openTaskPreview}
        {...listeners}
        {...attributes}
        {...divProps}
      >
        {isDragging && <div className="absolute inset-0 z-20 bg-[#E7E9EA]" />}
        {isUnread && (
          <TaskLabelContainer className="absolute top-0 right-0 rounded-bl-xl bg-gradients-danger px-2">
            <BellIcon />
          </TaskLabelContainer>
        )}
        {!!shortId && (
          <div className="mb-1 flex items-center gap-1.5">
            {isFormBased && <OrderIcon />}
            <span className="text-xs text-[#828282]">{shortId}</span>
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-sm leading-3 font-medium tracking-[0.14px] break-words text-dark">
            {highlightedText ? (
              <Highlighter
                autoEscape
                highlightClassName="text-primary bg-primary-50"
                highlightTag="span"
                textToHighlight={shortenedTitle}
                searchWords={[highlightedText]}
                onClick={onClick}
              />
            ) : (
              <span onClick={onClick}>{shortenedTitle}</span>
            )}
          </span>
          {dueDate && (
            <span
              className={cx(
                "self-start text-xs leading-2 tracking-[0.12px]",
                isOverdue
                  ? "rounded-md bg-tertiary-50 px-2 py-1 font-medium text-tertiary-700"
                  : "text-[#828282]"
              )}
            >
              {formatDateTimeLabel(dueDate)}
            </span>
          )}
        </div>
        {checklist && checklist.totalItemsCount > 0 && !isFutureTask && (
          <div className="my-2 flex items-center gap-2 py-2">
            {checklist.totalItemsCount > 0 && (
              <div className="flex items-center gap-2">
                <div
                  className={cx(
                    "flex items-center gap-2 font-medium",
                    checklist.isOverdue
                      ? "rounded-md bg-tertiary-50 px-2 py-1 text-tertiary-700"
                      : "text-grey"
                  )}
                >
                  <CheckBlankIcon
                    className={cx(
                      checklist.isOverdue
                        ? "text-gradients-danger"
                        : "text-grey-600"
                    )}
                  />
                  <span className="text-xs leading-[14px]">
                    {checklist.completedItemsCount}/{checklist.totalItemsCount}
                  </span>
                </div>
                <div className="relative h-2.5 w-20 overflow-hidden rounded-lg bg-dark-100">
                  <div
                    className="absolute inset-y-0 left-0 rounded-lg bg-grey-400"
                    style={{
                      width: `${
                        (checklist.totalItemsCount === 0
                          ? 0
                          : checklist.completedItemsCount /
                            checklist.totalItemsCount) * 100
                      }%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {(!!recurrence || (fields && fields.length > 0)) && (
          <div className="flex flex-wrap gap-2 pt-2">
            {recurrence && (
              <>
                {recurrence.isFutureTask ? (
                  <TaskCardPill
                    label={t({
                      id: "tasks-page.scheduled",
                      defaultMessage: "Scheduled",
                    })}
                    textColor="#A24A0F"
                    backgroundColor="#FFF2E8"
                    startIcon={<ScheduleMdIcon />}
                  />
                ) : (
                  <TaskCardPill
                    label={t({
                      id: "tasks-page.recurring-task",
                      defaultMessage: "Recurring task",
                    })}
                    textColor="#9F2E29"
                    backgroundColor="#FAECEE"
                    startIcon={<RecurringIcon />}
                    endIcon={recurrence.isPaused && <RecurringWarningIcon />}
                  />
                )}
              </>
            )}
            {fields?.map((field) => (
              <TaskCardPill
                key={field.id}
                label={field.value}
                hint={field.hint}
                backgroundColor={field.backgroundColor}
                textColor={field.textColor}
                startIcon={field.startIcon}
              />
            ))}
          </div>
        )}
      </InteractiveContainer>
    </>
  );
};
